// SaveToKnowledgeBase.js - Version avec callback enrichi pour les indicateurs

import React, { useState } from 'react';
import { Save, Check, X, FileText, BookOpen } from 'lucide-react';
import { doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';

const SaveToKnowledgeBase = ({ 
  messageContent, 
  messageContext = {},
  currentStepId,
  competenceId,
  onSaveSuccess,
  onSaveError 
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [selectedRideau, setSelectedRideau] = useState('ai_recommendations');
  const [customTitle, setCustomTitle] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Rideaux disponibles
  const availableRideaux = [
    { 
      id: 'decisions', 
      label: 'Décisions & Actions', 
      icon: '✅',
      description: 'Décisions prises et actions réalisées'
    },
    { 
      id: 'questions', 
      label: 'Questions & Défis', 
      icon: '❓',
      description: 'Questions ouvertes et difficultés'
    },
    { 
      id: 'ai_recommendations', 
      label: 'Recommandations', 
      icon: '💡',
      description: 'Conseils, bonnes pratiques et ressources d\'Aladinn'
    },
    { 
      id: 'resources', 
      label: 'Ressources', 
      icon: '📚',
      description: 'Liens, documents et résultats obtenus'
    }
  ];

  const handleSaveToKnowledgeBase = async () => {
    const currentUser = auth.currentUser;
    
    if (!currentUser?.uid || !competenceId || !currentStepId || !messageContent) {
      console.error('❌ Données manquantes:', { 
        currentUser: !!currentUser, 
        competenceId, 
        currentStepId, 
        messageContent: !!messageContent 
      });
      if (onSaveError) {
        onSaveError('Données manquantes pour la sauvegarde');
      }
      return;
    }

    setIsSaving(true);

    try {
      console.log('💾 Début sauvegarde mémo dans rideau:', selectedRideau);
      
      const docRef = doc(db, 'users', currentUser.uid, 'competences', competenceId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Document de compétence introuvable');
      }

      const currentData = docSnap.data();
      const currentStepNotes = currentData.stepNotes || {};
      const stepData = currentStepNotes[currentStepId] || {};

      console.log('📂 Données actuelles stepNotes:', stepData);

      // Préparer le contenu à sauvegarder
      const title = customTitle.trim() || `Mémo du ${new Date().toLocaleDateString('fr-FR')}`;
      const timestamp = new Date().toLocaleString('fr-FR');
      const rideauLabel = availableRideaux.find(r => r.id === selectedRideau)?.label || selectedRideau;
      
      // Format du mémo
      const memoContent = `
### 📝 ${title}
**Date:** ${timestamp}
**Catégorie:** ${rideauLabel}
**Source:** Conversation avec Aladinn
${messageContext.currentStepTitle ? `**Étape:** ${messageContext.currentStepTitle}` : ''}

---

${messageContent}

---
`;

      // Récupérer le contenu existant du rideau
      const existingContent = stepData[selectedRideau] || '';
      
      console.log('📄 Contenu existant dans', selectedRideau, ':', existingContent.length, 'caractères');
      
      // Ajouter le nouveau mémo
      const updatedContent = existingContent 
        ? `${existingContent}\n\n${memoContent}`
        : memoContent;

      console.log('📝 Nouveau contenu total:', updatedContent.length, 'caractères');

      // Mettre à jour les données de l'étape
      const updatedStepData = {
        ...stepData,
        [selectedRideau]: updatedContent,
        stepId: currentStepId,
        lastUpdate: selectedRideau,
        updatedAt: timestamp
      };

      const updatedStepNotes = {
        ...currentStepNotes,
        [currentStepId]: updatedStepData
      };

      console.log('💾 Sauvegarde dans Firebase...');

      // Sauvegarder dans Firebase
      await updateDoc(docRef, {
        stepNotes: updatedStepNotes,
        stepNotesUpdatedAt: serverTimestamp()
      });

      console.log('✅ Mémo sauvegardé avec succès dans rideau:', selectedRideau);
      
      setSaveSuccess(true);
      
      // 🆕 Callback enrichi avec toutes les infos nécessaires
      if (onSaveSuccess) {
        onSaveSuccess({
          rideau: selectedRideau,           // ID du rideau
          rideauLabel: rideauLabel,         // Nom du rideau
          title: title,                     // Titre de la note
          timestamp: timestamp,             // Timestamp
          stepId: currentStepId,            // ID de l'étape
          section: rideauLabel              // Pour compatibilité
        });
      }

      // Fermer la modal après 1.5 secondes
      setTimeout(() => {
        setShowSaveModal(false);
        setSaveSuccess(false);
        setCustomTitle('');
      }, 1500);

    } catch (error) {
      console.error('❌ Erreur sauvegarde mémo:', error);
      
      if (onSaveError) {
        onSaveError(error.message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!currentStepId) {
    return null;
  }

  return (
    <>
      {/* Bouton de sauvegarde */}
      <button
        onClick={() => setShowSaveModal(true)}
        className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200 group relative"
        title="Sauvegarder dans la base de connaissances"
      >
        <BookOpen size={16} className="text-gray-500 group-hover:text-blue-600" />
        <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
          Sauvegarder dans les notes
        </span>
      </button>

      {/* Modal de sauvegarde */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Sauvegarder dans les notes
                </h3>
              </div>
              <button
                onClick={() => setShowSaveModal(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                disabled={isSaving}
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            {/* Contenu */}
            <div className="p-4 space-y-4">
              {/* Aperçu du contenu */}
              <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                <p className="text-sm text-gray-700 line-clamp-4">
                  {messageContent.substring(0, 200)}...
                </p>
              </div>

              {/* Titre personnalisé */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre du mémo (optionnel)
                </label>
                <input
                  type="text"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  placeholder="Ex: Recommandations pour débuter"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isSaving}
                />
              </div>

              {/* Sélection du rideau */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Sauvegarder dans
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {availableRideaux.map(rideau => (
                    <button
                      key={rideau.id}
                      onClick={() => setSelectedRideau(rideau.id)}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        selectedRideau === rideau.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                      disabled={isSaving}
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-2xl flex-shrink-0">{rideau.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-semibold mb-1 ${
                            selectedRideau === rideau.id ? 'text-blue-900' : 'text-gray-900'
                          }`}>
                            {rideau.label}
                          </div>
                          <div className="text-xs text-gray-500">
                            {rideau.description}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Message de succès */}
              {saveSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                  <Check className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium text-green-800">
                    Mémo sauvegardé avec succès !
                  </span>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowSaveModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={isSaving}
              >
                Annuler
              </button>
              <button
                onClick={handleSaveToKnowledgeBase}
                disabled={isSaving || !messageContent}
                className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  isSaving || !messageContent
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Sauvegarder
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SaveToKnowledgeBase;